import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { PageHead, apiErr, fmtDate, thisYM, useMerendaMeta } from "@/merenda/common";
import { ImportReview } from "@/merenda/ImportReview";

const ST = { PROCESSANDO: "bg-blue-100 text-blue-700", CONFERENCIA: "bg-amber-100 text-amber-800", CONFIRMADA: "bg-green-100 text-green-700", DESCARTADA: "bg-slate-200 text-slate-600", ERRO: "bg-red-100 text-red-700" };
const LB = { PROCESSANDO: "Lendo com IA…", CONFERENCIA: "Aguardando conferência", CONFIRMADA: "Confirmada", DESCARTADA: "Descartada", ERRO: "Erro na leitura" };

export const KindPanel = ({ kind, meta, foods, scopeParams, onConfirmed, onFoodsChanged }) => {
  const [ym, setYm] = useState(thisYM());
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/merenda/imports", { params: { kind, ...scopeParams } }).then((r) => setList(r.data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!list.some((i) => i.status === "PROCESSANDO")) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [list]); // eslint-disable-line react-hooks/exhaustive-deps
  const upload = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("kind", kind); fd.append("ym", ym);
      if (scopeParams.school_id) fd.append("school_id", scopeParams.school_id);
      await api.post("/merenda/imports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Arquivo enviado. A IA está lendo — a conferência aparece aqui quando terminar."); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const openImp = async (i) => { const { data } = await api.get(`/merenda/imports/${i.id}`, { params: scopeParams }); setOpen(data); };
  return (
    <div>
      <Card className="mb-4 flex flex-wrap items-end gap-3 border-slate-200 p-4">
        <div><label className="text-xs text-slate-500">Mês do documento</label><Input type="month" value={ym} onChange={(e) => setYm(e.target.value)} className="w-44" data-testid={`import-${kind}-month`} /></div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600" data-testid={`import-${kind}-upload`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Enviar PDF/fotos
          <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={upload} disabled={busy} data-testid={`import-${kind}-file`} />
        </label>
        <p className="text-xs text-slate-500">{kind === "MENU" ? "Gera o cardápio do mês após sua conferência." : "Guarda o consumo e os atendimentos do mês como histórico para as médias. Não altera o estoque atual."}</p>
      </Card>
      <div className="grid gap-2">
        {list.map((i) => (
          <Card key={i.id} className="flex flex-wrap items-center gap-3 border-slate-200 p-3" data-testid={`import-row-${i.id}`}>
            <div className="flex-1 text-sm"><b>{i.ym}</b> · {i.files.map((f) => f.filename).join(", ")} <span className="text-xs text-slate-400">· {fmtDate(i.created_at?.slice(0, 10))}</span>{i.error && <div className="text-xs text-red-600">{i.error}</div>}</div>
            <Badge className={ST[i.status]}>{LB[i.status]}</Badge>
            {i.status === "CONFERENCIA" && <Button size="sm" onClick={() => openImp(i)} data-testid={`import-review-${i.id}`}>Conferir</Button>}
          </Card>
        ))}
        {!list.length && <Card className="border-dashed p-8 text-center text-sm text-slate-400">Nenhuma importação.</Card>}
      </div>
      {open && <ImportReview imp={open} meta={meta} foods={foods} scopeParams={scopeParams} onFoodsChanged={onFoodsChanged} onClose={() => setOpen(null)} onDone={() => { setOpen(null); load(); onConfirmed?.(); }} />}
    </div>
  );
};

export default function ImportarMerenda() {
  const { meta, foods, scopeParams, reload } = useMerendaMeta();
  if (!meta) return <div className="grid min-h-[40vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-blue-700" /></div>;
  return (
    <div data-testid="merenda-import-page">
      <PageHead title="Importar com IA" subtitle="A IA lê PDFs escaneados e fotos. Nada é gravado sem a sua conferência; valores ilegíveis ficam em branco." />
      <Tabs defaultValue="MENU">
        <TabsList className="mb-4"><TabsTrigger value="MENU" data-testid="import-tab-menu">Cardápio</TabsTrigger><TabsTrigger value="HISTORY" data-testid="import-tab-history">Mapas antigos</TabsTrigger></TabsList>
        <TabsContent value="MENU"><KindPanel kind="MENU" meta={meta} foods={foods} scopeParams={scopeParams} onFoodsChanged={reload} /></TabsContent>
        <TabsContent value="HISTORY"><KindPanel kind="HISTORY" meta={meta} foods={foods} scopeParams={scopeParams} /></TabsContent>
      </Tabs>
    </div>
  );
}
